import React, { useState, useEffect } from 'react';
import { PhoneCall, PhoneOff, Clock, Calendar, MessageCircle, FileText, Download, Search, Filter, RefreshCw } from 'lucide-react';
import api from '../../services/api';

function HistoricoLigacoes() {
  const [ligacoes, setLigacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLigacao, setSelectedLigacao] = useState(null);
  const [conversa, setConversa] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    produto: '',
    dataInicio: '',
    dataFim: ''
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    carregarLigacoes();
  }, [page, filters]);

  const carregarLigacoes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        ...(searchTerm && { numero: searchTerm }),
        ...filters
      });

      const response = await api.get(`/sdr-voz/historico?${params}`);
      setLigacoes(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    } finally {
      setLoading(false);
    }
  };

  const carregarDetalhes = async (ligacao) => {
    try {
      const response = await api.get(`/sdr-voz/historico/${ligacao.id}`);
      setSelectedLigacao(response.data.data);
      setConversa(response.data.data.conversa || []);
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
    }
  };

  const exportarExcel = () => {
    // Implementar exportação para Excel
    window.print(); // Placeholder
  };

  const formatarStatus = (status) => {
    const statusMap = {
      'pendente': { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
      'ligando': { label: 'Ligando', color: 'bg-blue-100 text-blue-800' },
      'concluida': { label: 'Concluída', color: 'bg-green-100 text-green-800' },
      'falha': { label: 'Falha', color: 'bg-red-100 text-red-800' },
      'cancelado': { label: 'Cancelado', color: 'bg-gray-100 text-gray-800' }
    };
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  const formatarDuracao = (segundos) => {
    if (!segundos) return '-';
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;

    if (horas > 0) {
      return `${horas}h ${minutos}m ${segs}s`;
    }
    if (minutos > 0) {
      return `${minutos}m ${segs}s`;
    }
    return `${segs}s`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Histórico de Ligações</h1>
          <p className="text-sm text-gray-500 mt-0.5">Visualize e gerencie todas as ligações realizadas pelo SDR</p>
        </div>
        <div className="flex gap-3">
          <button onClick={exportarExcel} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            <Download size={16} /> Exportar Excel
          </button>
          <button onClick={carregarLigacoes} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            <RefreshCw size={16} /> Atualizar
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por número..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="ligando">Ligando</option>
            <option value="concluida">Concluída</option>
            <option value="falha">Falha</option>
            <option value="cancelado">Cancelado</option>
          </select>

          <select
            value={filters.produto}
            onChange={(e) => setFilters({...filters, produto: e.target.value})}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">Todos os produtos</option>
            <option value="Ingarden">Ingarden</option>
            <option value="Lumiere Residence Club">Lumiere</option>
            <option value="Benjamin Constant Duplex">Benjamin</option>
          </select>

          <div className="flex gap-2">
            <button
              onClick={() => setFilters({...filters, dataInicio: '', dataFim: ''})}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Ligações */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-700 text-sm">
                  Ligações ({ligacoes.length}/{total})
                </h2>
                <span className="text-xs text-gray-500">
                  Página {page} de {Math.ceil(total / itemsPerPage)}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : ligacoes.length === 0 ? (
              <div className="py-12 text-center text-gray-300">
                <PhoneOff size={48} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">Nenhuma ligação encontrada</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {ligacoes.map(ligacao => (
                  <div
                    key={ligacao.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => carregarDetalhes(ligacao)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                          ligacao.status === 'concluida' ? 'bg-green-100 text-green-600' :
                          ligacao.status === 'falha' ? 'bg-red-100 text-red-500' :
                          ligacao.status === 'ligando' ? 'bg-blue-100 text-blue-600' :
                          'bg-yellow-100 text-yellow-600'
                        }`}>
                          {ligacao.status === 'concluida' ? <PhoneCall size={14} /> :
                           ligacao.status === 'falha' ? <PhoneOff size={14} /> :
                           <PhoneCall size={14} />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {ligacao.nome || 'Lead'}
                          </p>
                          <p className="text-xs text-gray-400 font-mono">
                            {ligacao.numero}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">{ligacao.produto}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(ligacao.data_criacao).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Paginação */}
            {total > itemsPerPage && (
              <div className="px-4 py-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-200 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-500">
                    Página {page} de {Math.ceil(total / itemsPerPage)}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(Math.ceil(total / itemsPerPage), page + 1))}
                    disabled={page === Math.ceil(total / itemsPerPage)}
                    className="px-3 py-1 border border-gray-200 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Detalhes da Ligação */}
        <div className="lg:col-span-1">
          {selectedLigacao ? (
            <div className="bg-white rounded-xl shadow">
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-semibold text-gray-700 text-sm">Detalhes da Ligação</h2>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Lead</p>
                  <p className="text-sm text-gray-900">{selectedLigacao.nome}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Telefone</p>
                  <p className="text-sm text-gray-900 font-mono">{selectedLigacao.numero}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Produto</p>
                  <p className="text-sm text-gray-900">{selectedLigacao.produto}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${formatarStatus(selectedLigacao.status).color}`}>
                    {formatarStatus(selectedLigacao.status).label}
                  </span>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Duração</p>
                  <p className="text-sm text-gray-900">{formatarDuracao(selectedLigacao.duracao_segundos)}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Data da Ligação</p>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedLigacao.data_criacao).toLocaleString('pt-BR')}
                  </p>
                </div>

                {selectedLigacao.transcricao && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Transcrição</p>
                    <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                      <p className="text-xs text-gray-700 whitespace-pre-wrap">{selectedLigacao.transcricao}</p>
                    </div>
                  </div>
                )}

                {selectedLigacao.resumo_ia && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Resumo IA</p>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-blue-800 whitespace-pre-wrap">{selectedLigacao.resumo_ia}</p>
                    </div>
                  </div>
                )}

                {conversa.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Conversa</p>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {conversa.map(msg => (
                        <div key={msg.id} className={`p-2 rounded-lg text-xs ${
                          msg.remetente === 'cliente' ? 'bg-gray-100' : 'bg-blue-100'
                        }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-semibold ${
                              msg.remetente === 'cliente' ? 'text-gray-700' : 'text-blue-700'
                            }`}>
                              {msg.remetente === 'cliente' ? 'Cliente' : 'Bot SDR'}
                            </span>
                            <span className="text-gray-500">
                              {new Date(msg.timestamp).toLocaleTimeString('pt-BR')}
                            </span>
                          </div>
                          <p className="text-gray-700">{msg.conteudo}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow p-8 text-center">
              <MessageCircle size={48} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-500">Selecione uma ligação para ver os detalhes</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HistoricoLigacoes;